import {
  Autocomplete,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import HeaderNavigation from "../../../../../Componants/Common/header Navigation/HeaderNavigation";
import CommonButton from "../../../../../Componants/Common/Button";
import ReportTabs from "../../../../../Componants/Common/ReportTabs";
import { Calendar } from "../../../../../Componants/Common/Calender";
import {
  fetchBrandList,
  fetchCategoryList,
  fetchModelList,
  getStateList,
  fetchJobReport,
} from "../../../../../API Service/apiService";
import Loader from "../../../../../Componants/Loader/Loader";
import toast, { Toaster } from "react-hot-toast";
import Paginate from "../../../../../Componants/Common/Paginate";
import { formatDate } from "../../../../../utils/dateFormatter";

const dateTypes = [{ label: "Creation" }, { label: "Closure" }];
const cells = [
  "Job Number",
  "Job Date",
  "Model",
  "Creation Date",
  "Closure Date",
  "JobsheetType",
  "Payment",
  "Warranty Status"
];

const JobReport = () => {
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(1);
  const [dataSize, setDataSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableData, setTableData] = useState([]);

  // States for dropdowns
  const [brandList, setBrandList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [states, setStates] = useState([]);

  // Selected values
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // Add selected date type state
  const [selectedDateType, setSelectedDateType] = useState(null);

  const [searchParams, setSearchParams] = useState({
    type: "",
    startDate: "",
    endDate: "",
    modelId: "",
    stateName: "",
    brandId: "",
    categoryId: "",
    limit: 10,
    page: 1,
  });

  const handleDateChange = (fieldName, date, formattedDate) => {
    if (fieldName === "fromDate") {
      setFromDate(date);
      setSearchParams(prev => ({ ...prev, startDate: formattedDate }));
    } else if (fieldName === "toDate") {
      if (fromDate && date < fromDate) {
        toast.error("To Date cannot be less than From Date");
        return;
      }
      setToDate(date);
      setSearchParams(prev => ({ ...prev, endDate: formattedDate }));
    }
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [brandResponse, stateResponse] = await Promise.all([
        fetchBrandList(),
        getStateList()
      ]);
      setBrandList(brandResponse.data.brandList || []);
      setStates(stateResponse.data.states || []);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to fetch initial data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories when brand changes
  const fetchCategories = async () => {
    if (!selectedBrand?._id) return;
    try {
      setLoading(true);
      const response = await fetchCategoryList(selectedBrand._id, "", 1, 10, true);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch models when category changes
  const fetchModels = async () => {
    if (!selectedBrand?._id || !selectedCategory?._id) return;
    try {
      setLoading(true);
      const response = await fetchModelList("", 1, 10, selectedBrand._id, selectedCategory._id);
      setModels(response.data.models || []);
    } catch (error) {
      console.error("Error fetching models:", error);
    } finally {
      setLoading(false);
    }
  };

  const getJobReport = async (params = searchParams) => {
    try {
      setLoading(true);
      const response = await fetchJobReport(params);
      console.log('Job Report Response:', response); // Debug log
      
      if (response?.data?.data?.jobReport) {
        setTableData(response.data.data.jobReport);
        setTotalRecords(response.data.data.pagination?.totalJobs || 0);
      } else {
        setTableData([]);
        setTotalRecords(0);
        toast.error("No data available");
      }
    } catch (error) {
      console.error("Error fetching job report:", error);
      toast.error(error.message || "Failed to fetch data");
      setTableData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setPage(1);
      const updatedParams = {
        ...searchParams,
        page: 1,
        limit: dataSize,
      };
      setSearchParams(updatedParams);
      await getJobReport(updatedParams);
    } catch (error) {
      console.error("Error in search:", error);
      toast.error("Search failed. Please try again.");
    }
  };

  const handleReset = async () => {
    // First reset all the states
    setFromDate(null);
    setToDate(null);
    setSelectedBrand(null);
    setSelectedCategory(null);
    setSelectedModel(null);
    setSelectedState(null);
    setSelectedDateType(null);
    
    // Reset search params
    const resetParams = {
      type: "",
      startDate: "",
      endDate: "",
      modelId: "",
      stateName: "",
      brandId: "",
      categoryId: "",
      limit: 10,
      page: 1,
    };
    
    setSearchParams(resetParams);
    setPage(1);
    
    // Reset table data
    setTableData([]);
    setTotalRecords(0);
    
    // Fetch fresh data with reset params
    try {
      await getJobReport(resetParams);
    } catch (error) {
      console.error("Error resetting data:", error);
      toast.error("Failed to reset data");
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(prev => ({ ...prev, page: newPage }));
    getJobReport({ ...searchParams, page: newPage });
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        // Fetch initial data
        await Promise.all([
          fetchInitialData(),
          getJobReport() // Get initial report data
        ]);
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error("Failed to initialize data");
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [selectedBrand]);

  useEffect(() => {
    fetchModels();
  }, [selectedCategory]);

  return (
    <Grid container>
      {loading && <Loader />}
      <Toaster position="top-center" reverseOrder={false} />
      <HeaderNavigation value={"Job Report"} />
      <ReportTabs />
      
      {/* Search Form */}
      <Grid
        container
        gap={5}
        sx={{
          background: "#F5F8FF",
          margin: "10px 10px",
          width: "100%",
          borderRadius: "5px",
          padding: "12px",
          fontWeight: 600,
        }}
      >
        <Grid container spacing={5}>
          <Grid item>
            <Autocomplete
              options={dateTypes}
              getOptionLabel={(option) => option.label}
              value={selectedDateType}
              onChange={(event, value) => {
                setSelectedDateType(value);
                setSearchParams(prev => ({
                  ...prev,
                  type: value ? value.label : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Date Type"
                  variant="standard"
                  style={{ width: "12rem" }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Calendar
              value={fromDate}
              handleChange={handleDateChange}
              fieldName="fromDate"
              label="From Date"
            />
          </Grid>
          <Grid item>
            <Calendar
              value={toDate}
              handleChange={handleDateChange}
              fieldName="toDate"
              label="To Date"
            />
          </Grid>
        </Grid>

        <Grid container spacing={5}>
          <Grid item>
            <Autocomplete
              options={brandList}
              getOptionLabel={(option) => option.brandName || ""}
              value={selectedBrand}
              onChange={(event, value) => {
                setSelectedBrand(value);
                setSelectedCategory(null);
                setSelectedModel(null);
                setSearchParams(prev => ({
                  ...prev,
                  brandId: value?._id || "",
                  categoryId: "",
                  modelId: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  variant="standard"
                  style={{ width: "12rem" }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.categoryName || ""}
              value={selectedCategory}
              onChange={(event, value) => {
                setSelectedCategory(value);
                setSelectedModel(null);
                setSearchParams(prev => ({
                  ...prev,
                  categoryId: value?._id || "",
                  modelId: "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product Category"
                  variant="standard"
                  style={{ width: "12rem" }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Autocomplete
              options={models}
              getOptionLabel={(option) => option.modelName || ""}
              value={selectedModel}
              onChange={(event, value) => {
                setSelectedModel(value);
                setSearchParams(prev => ({
                  ...prev,
                  modelId: value?._id || "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model"
                  variant="standard"
                  style={{ width: "12rem" }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Autocomplete
              options={states}
              getOptionLabel={(option) => option.stateName || ""}
              value={selectedState}
              onChange={(event, value) => {
                setSelectedState(value);
                setSearchParams(prev => ({
                  ...prev,
                  stateName: value?._id || "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  variant="standard"
                  style={{ width: "12rem" }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid container gap={2}>
          <CommonButton name={"Search"} handleOnClick={handleSearch} />
          <CommonButton name={"Reset"} handleOnClick={handleReset} />
        </Grid>
      </Grid>

      {/* Results Table */}
      <Grid
        container
        sx={{
          background: "#F5F8FF",
          margin: "0 10px",
          width: "100%",
          borderRadius: "5px",
          padding: "12px",
          fontWeight: 600,
        }}
      >
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
            <TableHead>
              <TableRow>
                {cells.map((cell, index) => (
                  <TableCell
                    key={index}
                    sx={{ color: "white", textAlign: "center" }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.length > 0 ? (
                tableData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell align="center">{row.jobNumber || "-"}</TableCell>
                    <TableCell align="center">
                      {row.preferredDate ? formatDate(row.preferredDate) : "-"}
                    </TableCell>
                    <TableCell align="center">{row.modelName || "-"}</TableCell>
                    <TableCell align="center">
                      {row.createdAt ? formatDate(row.createdAt) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {row.closureDate ? formatDate(row.closureDate) : "-"}
                    </TableCell>
                    <TableCell align="center">{row.repairAction || "-"}</TableCell>
                    <TableCell align="center">{row.paymentAmount || "-"}</TableCell>
                    <TableCell align="center">{row.warrantyStatus || "-"}</TableCell>
                  </TableRow>
                  
                  
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={cells.length} align="center" sx={{ py: 3 }}>
                    No Data Available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid
          container
          sx={{
            marginTop: "1rem",
            marginBottom: "1rem",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paginate
            page={page}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            dataSize={dataSize}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default JobReport;
